import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';


/**
 * ContextMenu - Right-click context menu for cells and rows
 * 
 * @param {Object|null} contextMenu - Context menu state object with position and data
 * @param {function} onClose - Callback to close the menu
 * @param {function} onRemoveRow - Callback when row delete is clicked (algae/zooplankton)
 * @param {function} onRemoveZooplanktonRow - Callback when zooplankton row delete is clicked
 * @param {function} onRemoveColumn - Callback when column delete is clicked
 * @param {function} onColorOverride - Callback when color override is selected
 */
export default function ContextMenu({
  contextMenu,
  onClose,
  onRemoveRow,
  onRemoveZooplanktonRow,
  onRemoveColumn,
  onColorOverride
}) {
  if (!contextMenu) return null;

  const handleRowDelete = () => {
    if (contextMenu.isZooplankton) {
      // Zooplankton deletion
      const organism = contextMenu.organism;
      const confirmMessage = organism.isCustom 
        ? `Slette egendefinert rad: ${organism.sampleType} - ${organism.organism}?`
        : `Fjerne "${organism.sampleType} - ${organism.organism}" fra skjemaet? (Standard rad)`;
      
      if (window.confirm(confirmMessage)) {
        onRemoveZooplanktonRow(contextMenu.organismKey);
        toast.success('Rad slettet');
      } else {
      }
    } else {
      // Algae deletion
      onRemoveRow(contextMenu.organismKey, contextMenu.organism);
    }
    onClose();
  };

  const handleColumnDelete = () => {
    if (contextMenu.location) {
      onRemoveColumn(contextMenu.location);
      onClose();
    }
  };

  const handleColorOverride = (color) => {
    onColorOverride(color);
    onClose();
  };

  return (
    <>
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      <div 
        className="fixed z-50 bg-white border border-slate-300 rounded-lg shadow-xl py-2 min-w-48"
        style={{ left: contextMenu.x, top: contextMenu.y }}
      >
        {contextMenu.isRowContext ? (
          // Row context menu
          <>
            <div className="px-3 py-2 text-xs font-medium text-slate-600 border-b border-slate-200">
              Radmeny
            </div>
            <button 
              onClick={handleRowDelete}
              className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-600"
            >
              <Trash2 className="w-3 h-3" />
              <span>
                Slett rad ({contextMenu.isZooplankton 
                  ? `${contextMenu.organism?.sampleType} - ${contextMenu.organism?.organism}` 
                  : contextMenu.organism?.species || ''})
              </span>
            </button>
          </>
        ) : (
          // Cell context menu
          <>
            <div className="px-3 py-2 text-xs font-medium text-slate-600 border-b border-slate-200">
              Cellemeny
            </div>
            
            {/* Delete column option */}
            <button 
              onClick={handleColumnDelete}
              className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-600 border-b border-slate-200"
            >
              <Trash2 className="w-3 h-3" />
              <span>Slett hele kolonnen ({contextMenu.location?.split(' - ')[1] || ''})</span>
            </button>
            
            <div className="px-3 py-2 text-xs font-medium text-slate-600 border-b border-slate-200">
              Overstyr farge
            </div>
            <button 
              onClick={() => handleColorOverride(null)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2"
            >
              <div className="w-3 h-3 border border-slate-300 rounded bg-white"></div>
              <span>Auto (følg grenseverdi)</span>
            </button>
            <button 
              onClick={() => handleColorOverride('green')}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2"
            >
              <div className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded"></div>
              <span>Grønn (trygt)</span>
            </button>
            <button 
              onClick={() => handleColorOverride('yellow')}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2"
            >
              <div className="w-3 h-3 bg-amber-100 border border-amber-300 rounded"></div>
              <span>Gul (overvåk)</span>
            </button>
            <button 
              onClick={() => handleColorOverride('red')}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2"
            >
              <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
              <span>Rød (kritisk)</span>
            </button>
          </>
        )}
      </div>
    </>
  );
}
